import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { GridColumnSettings } from 'src/app/models/gridColumnSettings.model';
import { GridView } from 'src/app/models/gridView.model';
import { GridViewType } from 'src/app/enums/gridViewType.enum';
import type { GridViewService } from 'src/app/services/grid-view.service';
import { Observable, map } from 'rxjs';

export function saveGridView(
  gridViewService: Pick<GridViewService, 'update' | 'create'>,
  view: GridView,
): Observable<GridView | undefined> {
  return view.id ? gridViewService.update(view).pipe(map(() => view)) : gridViewService.create(view);
}

export function synthesizeDefaultView(
  fieldConfig: Record<string, ColumnDetails> | null | undefined,
  objectId: string,
  opts: {
    viewName: string;
    widgetMode?: boolean;
    orgLevelId?: string;
    fallbackColumns?: GridColumnSettings[];
    fallbackOrder?: string[];
  }
): GridView {
  let columns: GridColumnSettings[] = [];

  if (fieldConfig && Object.keys(fieldConfig).length > 0) {
    columns = Object.entries(fieldConfig)
      .filter(([, details]) => details.isDefault)
      .map(([key], idx) => ({ property: key, visible: true, order: idx }));

    if (opts.fallbackOrder?.length) {
      columns.sort((a, b) => {
        const ai = opts.fallbackOrder!.indexOf(a.property);
        const bi = opts.fallbackOrder!.indexOf(b.property);
        const aPos = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
        const bPos = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
        return aPos - bPos;
      });
      columns = columns.map((col, idx) => ({ ...col, order: idx }));
    }

    if (columns.length === 0 && opts.fallbackColumns?.length) {
      columns = opts.fallbackColumns;
    }
  } else {
    columns = opts.fallbackColumns ?? [];
  }

  return {
    objectId,
    type: opts.widgetMode ? GridViewType.Dashboard : GridViewType.Grid,
    viewName: opts.viewName,
    isShared: false,
    isMobileView: false,
    organizationLevelId: opts.orgLevelId,
    settingsJson: { columns, filters: {} },
  };
}

export function mergeSavedColumns<T extends { property: string; order: number }>(
  saved: GridColumnSettings[],
  availableProps: string[],
  build: (property: string, order: number, visible?: boolean) => T
): T[] {
  const mapped = saved.map(col => build(col.property, col.order, col.visible));
  const savedProperties = new Set(mapped.map(col => col.property));
  let nextOrder = mapped.length > 0 ? Math.max(...mapped.map(col => col.order)) + 1 : 0;

  for (const property of availableProps) {
    if (!savedProperties.has(property)) {
      mapped.push(build(property, nextOrder++, false));
    }
  }
  return mapped;
}
